import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  OFFER_CODE_MAX_LENGTH,
  OFFER_CODE_NOTE_MAX_LENGTH,
} from '../iap-offer-code-mapping/iap-offer-code-mapping';
import { IapStoreSchema } from '../iap-store/iap-store';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * Creating one mapping.
 *
 * `discountRuleId`, `productKey` and `store` together are what the unique index
 * is on — one code per rule per product per store — and all three are **absent
 * from the update schema** for `SubscriptionProductCreateSchema`'s reason: they
 * are the identity of the mapping, not attributes of it. Re-pointing a code at a
 * different rule is a different mapping, and editing it in place would silently
 * move a discount somebody has already been shown.
 */
export const IapOfferCodeMappingCreateSchema = z
  .object({
    discountRuleId: UuidSchema,
    productKey: ProductKeySchema,
    store: IapStoreSchema,
    /**
     * Not `.toUpperCase()`d: Apple's codes are case-sensitive and a redemption
     * URL built from a helpfully-normalised code silently fails to redeem.
     */
    offerCode: z.string().trim().min(1).max(OFFER_CODE_MAX_LENGTH),
    isActive: z.boolean().default(true),
    note: z.string().trim().max(OFFER_CODE_NOTE_MAX_LENGTH).nullable().default(null),
  })
  .strict();

export type IapOfferCodeMappingCreateRequest = z.infer<typeof IapOfferCodeMappingCreateSchema>;

/** What may change afterwards: the code itself, whether it is offered, and why. */
export const IapOfferCodeMappingUpdateSchema = IapOfferCodeMappingCreateSchema.omit({
  discountRuleId: true,
  productKey: true,
  store: true,
})
  .partial()
  .strict();

export type IapOfferCodeMappingUpdateRequest = z.infer<typeof IapOfferCodeMappingUpdateSchema>;
